// src/components/TakeoverButton.jsx
import React, { useState, useEffect } from "react";
import ConfirmModal from "./ConfirmModal";
import { auth } from "../firebase";
import { getUserData } from "../services/userService";
import { useNotifications } from '../hooks/notificationsContext';

const PROTECTED_TICKERS = ["BND3", "BND7", "BND14", "INDX", "DEF", "XAU", "AUD", "BRL", "CNY", "EUR", "ZAR", "TOKE"];

export default function TakeoverButton({ companyData, style }) {
  const [canTakeover, setCanTakeover] = useState(false);
  const [cost, setCost] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [userData, setUserData] = useState(null);
  const [popupText, setPopupText] = useState("");
  const [hoverOpen, setHoverOpen] = useState(false);
  const { addNotification } = useNotifications();
  
  useEffect(() => {
    const checkEligibility = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      const user = await getUserData(currentUser.uid);
      setUserData(user);

      const isMajority = companyData.companyMajorityShareholder === user.userDisplayName;
      const isNotCEO = !user.userCEO;
      const lastVoteAgo = Date.now() - (user.userInitiatedVoteAt || 0);
      const voteCooldown = 14 * 24 * 60 * 60 * 1000;

      const unmetConditions = [];
      if (!isMajority) unmetConditions.push("REQUIRES MAJORITY SHAREHOLDER");
      if (!isNotCEO) unmetConditions.push("YOU ARE A CEO");
      if (lastVoteAgo < voteCooldown) {
        const daysLeft = Math.ceil((voteCooldown - lastVoteAgo) / (1000 * 60 * 60 * 24));
        unmetConditions.push(`COOLDOWN: ${daysLeft} DAYS`);
      }

      const marketCap = (companyData.companyStockVolume || 0) * (companyData.companyStockPrice || 0);
      const calcCost = Math.min(marketCap * 0.01, 10000);
      setCost(calcCost);

      setCanTakeover(unmetConditions.length === 0);
      setPopupText(
        unmetConditions.length > 0
          ? unmetConditions.join("\n")
          : `COST: $${calcCost.toLocaleString()}`
      );
    };

    checkEligibility();
  }, [companyData]);

  const handleClick = () => {
    if (canTakeover) setShowConfirm(true);
  };

const handleConfirm = async () => {
    setShowConfirm(false);
    if (!userData) return;

    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://initiateouster-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ticker: companyData.companyTicker }),
      });

      const data = await res.json();

      if (res.ok) {
        addNotification(data.status || "Takeover initiated.", "success");
      } else {
        addNotification(data.error || "Takeover request denied.", "error");
      }

    } catch (err) {
      console.error(err);
      addNotification("Connection lost. Failed to reach service.", "error");
    }
  };

  if (PROTECTED_TICKERS.includes(companyData.companyTicker)) return null;

  return (
    <>
      <div
        style={{ 
          display: "flex", 
          position: "relative",
          width: "100%",
          height: "100%"
        }}
        onMouseEnter={() => setHoverOpen(true)}
        onMouseLeave={() => setHoverOpen(false)}
      >
        <button
          className={`jetbrains-btn ${!canTakeover ? "disabled" : ""}`}
          disabled={!canTakeover}
          onClick={handleClick}
          style={{
            ...style, // Inherit grid sizing from parent
            cursor: canTakeover ? "pointer" : "not-allowed",
            opacity: canTakeover ? 1 : 0.3,
            width: "100%",
            height: "100%",
            textTransform: "uppercase",
            fontFamily: "'JetBrains Mono', monospace"
          }}
        >
          TAKEOVER
        </button>

{hoverOpen && popupText && (
  <div
    style={{
      position: "absolute",
      bottom: "calc(100% + 10px)",
      right: companyData?.companyTicker ? "0" : "auto", 
      left: companyData?.companyTicker ? "auto" : "50%",
      transform: companyData?.companyTicker ? "none" : "translateX(-50%)",
      
      background: "#111",
      color: "#50E3C2",
      border: "1px solid rgba(80,227,194,0.4)",
      borderRadius: 4,
      padding: "8px 12px",
      width: "max-content",
      maxWidth: "200px",
      whiteSpace: "pre-line",
      textAlign: "center",
      fontSize: "0.6rem",
      fontFamily: "JetBrains Mono",
      boxShadow: "0 4px 12px rgba(0,0,0,0.8)",
      zIndex: 9999,
      pointerEvents: "none"
    }}
  >
    {popupText}
    {/* Dynamic Arrow Placement */}
    <div
      style={{
        position: "absolute",
        top: "100%",
        right: companyData?.companyTicker ? "15px" : "auto",
        left: companyData?.companyTicker ? "auto" : "50%",
        width: 6,
        height: 6,
        background: "#111",
        borderLeft: "1px solid rgba(80,227,194,0.4)",
        borderBottom: "1px solid rgba(80,227,194,0.4)",
        transform: companyData?.companyTicker ? "rotate(-45deg)" : "translateX(-50%) rotate(45deg)",
        marginTop: "-3px",
      }}
    />
  </div>
)}
      </div>

      {showConfirm && (
        <ConfirmModal
          title="HOSTILE TAKEOVER"
          message={`OUSTER: Cost: $${cost.toLocaleString()}. Proceed with takeover of ${companyData.companyTicker}?`}
          confirmText="Confirm"
          cancelText="Cancel"
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
}